// eslint-disable-next-line import/no-extraneous-dependencies
import type { DefaultOptionType } from 'rc-select/lib/Select';

import type {
  IntFloatOptionValue,
  IntFloatValue,
  MeasureUnitIntFloatValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatField.types';
import type { IProductSpecificationOption } from 'src/types/reference/IProductSpecificationOption';
import {
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

export function getMeasureUnitIntFloatValueLabel(
  value: MeasureUnitIntFloatValue | Exclude<IProductSpecificationOption, number>
): string {
  return `${value.value}${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value.measureUnit]}`;
}

export function toIntFloatOptionValue(value?: IntFloatValue): IntFloatOptionValue {
  return value != null
    ? Array.isArray(value)
      ? value.map(getMeasureUnitIntFloatValueLabel)
      : getMeasureUnitIntFloatValueLabel(value)
    : null;
}

export function toIntFloatSingleValue(value?: IntFloatValue): string | undefined {
  return value != null
    ? Array.isArray(value)
      ? (value[0]?.value?.toString() ?? undefined)
      : value.value?.toString()
    : undefined;
}

export function toIntFloatValue(selectedOptions?: DefaultOptionType | DefaultOptionType[] | null): IntFloatValue {
  return selectedOptions != null
    ? Array.isArray(selectedOptions)
      ? selectedOptions.map((selectedOption) => ({
          value: selectedOption.numberValue as number,
          measureUnit: selectedOption.measureUnit as SpecificationMeasureUnit,
        }))
      : {
          value: selectedOptions.numberValue as number,
          measureUnit: selectedOptions.measureUnit as SpecificationMeasureUnit,
        }
    : null;
}

export function toIntFloatDefaultType(measureUnit: SpecificationMeasureUnit) {
  return (measureUnitOption: IProductSpecificationOption): DefaultOptionType => ({
    label: getMeasureUnitIntFloatValueLabel(measureUnitOption),
    value: measureUnitOption.label,

    // BaseType custom fields
    measureUnit,
    numberValue: measureUnitOption.value,
  });
}

export function toCustomIntFloatDefaultType(measureUnitValue: MeasureUnitIntFloatValue): DefaultOptionType {
  if (typeof measureUnitValue === 'number') {
    return {
      label: measureUnitValue,
      value: measureUnitValue,

      // BaseType custom fields
      measureUnit: SpecificationMeasureUnit.NONE,
      numberValue: measureUnitValue,

      isCustom: true,
    };
  }

  const label = getMeasureUnitIntFloatValueLabel(measureUnitValue);

  return {
    label,
    value: label,

    // BaseType custom fields
    measureUnit: measureUnitValue.measureUnit,
    numberValue: measureUnitValue.value,

    isCustom: true,
  };
}

export function areIntFloatValueAndOptionEqual(a: MeasureUnitIntFloatValue, b: IProductSpecificationOption): boolean {
  return a.measureUnit === b.measureUnit && a.value === b.value;
}
